import { Bell, Lock, Settings2, Shield, User, type LucideIcon } from "lucide-react";
import type { TabKey } from "../tabs";

/** One destination in the portal's service navigation. */
export interface NavItem {
  key: TabKey;
  icon: LucideIcon;
}

/**
 * A count beside a destination: something there is waiting for the reader.
 *
 * `label` is the words a screen reader needs after the number - "2" on its own
 * says nothing, "2 overdue" does. The tone is red only for money that is late;
 * everything else that waits is the brand colour.
 */
export interface NavBadge {
  count: number;
  tone: "default" | "destructive";
  label: string;
}

export type NavBadges = Partial<Record<TabKey, NavBadge>>;

/**
 * How many destinations the phone's tab bar draws before the rest fold into
 * "More". Five is the most a 360-pixel screen holds with a readable label under
 * each icon; with six or seven modules open, four stay and the fifth slot is
 * the door to the others.
 */
export const TAB_BAR_SLOTS = 5;

/** Split the navigation into what the tab bar shows and what sits behind "More". */
export function splitForTabBar<T>(items: readonly T[]): { shown: T[]; rest: T[] } {
  if (items.length <= TAB_BAR_SLOTS) return { shown: [...items], rest: [] };
  return { shown: items.slice(0, TAB_BAR_SLOTS - 1), rest: items.slice(TAB_BAR_SLOTS - 1) };
}

/**
 * The person's own screens, in the order and with the icons of the avatar
 * menu (`header/AccountMenu.tsx`), so the side list on those screens reads as
 * the same list the reader just picked from. Change one, change the other.
 */
export const PERSONAL_SCREENS: readonly NavItem[] = [
  { key: "profile", icon: User },
  { key: "preferences", icon: Settings2 },
  { key: "security", icon: Shield },
  { key: "notifications", icon: Bell },
  { key: "privacy", icon: Lock },
];

/**
 * The side list for the person's own screens: the ones this visit may open, in
 * the avatar menu's order. Anything the menu does not draw is left out too.
 */
export function personalNav(open: readonly TabKey[]): NavItem[] {
  return PERSONAL_SCREENS.filter((item) => open.includes(item.key));
}

/**
 * The element id of one personal screen on the portal's account page. The page
 * puts it on the section and the side list finds the section by it, so the two
 * files share this one spelling instead of a prop chain.
 */
export function personalSectionId(key: TabKey): string {
  return `account-${key}`;
}
